/**
 * qrAttribution — small helper for tracking which QR flyer converted.
 *
 * Two side effects, called once per page load when the URL carries
 * `?src=qr`:
 *   1. POST /api/qr/hit to log the anonymous scan.
 *   2. Stash `{kind, target_id, at}` in localStorage so the next
 *      order/donation POST can attach `qr_source` + `qr_kind`.
 *
 * Attribution expires after 60 min so a scanner who scrolls the
 * shop and eventually buys something unrelated doesn't get miscredited.
 */
import api from "./api";

const STORAGE_KEY = "reddog:qr_attribution";
const TTL_MS = 60 * 60 * 1000;

/**
 * Register a QR scan. Safe to call unconditionally — it no-ops when
 * the URL doesn't carry `?src=qr`. Returns the recorded attribution
 * (or null) so callers can react if they need to.
 */
export const registerQrHitFromUrl = async (kind, targetId) => {
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get("src") !== "qr" || !kind || !targetId) return null;
        const record = { kind, target_id: targetId, at: Date.now() };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
        // Fire-and-forget; a network hiccup shouldn't block page render.
        api.post("/qr/hit", {
            kind,
            target_id: targetId,
            referrer: document.referrer || null,
        }).catch(() => { /* silent */ });
        return record;
    } catch (_e) {
        return null;
    }
};

/**
 * Return the still-valid attribution, or null if none / expired.
 * Optionally filter to a specific target so we don't miscredit an
 * unrelated purchase.
 */
export const getQrAttribution = (matchTargetId = null) => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const rec = JSON.parse(raw);
        if (!rec || !rec.target_id || !rec.at) return null;
        if (Date.now() - rec.at > TTL_MS) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        if (matchTargetId && rec.target_id !== matchTargetId) return null;
        return rec;
    } catch (_e) {
        return null;
    }
};

export const clearQrAttribution = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* silent */ }
};

/**
 * Convenience helper: builds the fields to spread onto an order or
 * donation POST body. Returns an empty object when no attribution
 * applies, so it's always safe to spread.
 */
export const qrAttributionFields = (matchTargetId = null) => {
    const rec = getQrAttribution(matchTargetId);
    if (!rec) return {};
    return { qr_source: rec.target_id, qr_kind: rec.kind };
};
